import { inject } from '@angular/core';
import { CanActivateFn, RedirectFunction, Router, UrlTree } from '@angular/router';
import { LstAuthGuard, LstUserService } from '@savvaslearning/lst-auth';

import { environment } from '../../../environments/environment';
import { launchProductGrade } from '../auth/launch-params';
import {
  ALL_TAB_KEYS,
  ProductConfigService,
  TabKey,
} from '../services/product-config/product-config.service';
import { ProductVariantService } from '../services/product-variant/product-variant.service';
import { AuthStore } from '../stores/auth.store';
import { ClassStore } from '../stores/class.store';
import { ProjectionAppStore } from '../stores/projection-app.store';
import { UserStore } from '../stores/user.store';
import { ProgramResolutionService } from '../services/program-resolution/program-resolution.service';
import { roleLandingRoute } from '../../shared/utils/role-landing.util';

// Appends the attempted URL so the gate can send the user back where they aimed.
function routeWithReturnUrl(basePath: string, returnUrl: string | undefined): string {
  const cleanUrl = (returnUrl ?? '').trim();
  if (!cleanUrl || cleanUrl === basePath) return basePath;
  return `${basePath}?returnUrl=${encodeURIComponent(cleanUrl)}`;
}

// Legacy authenticates by pasting a TokenID at /token-entry; send them there until they have.
export const requireLegacyToken: CanActivateFn = (_route, state) => {
  if (!environment.useLegacyBackend) return true;

  const router = inject(Router);
  const authStore = inject(AuthStore);

  return authStore.tokenId()
    ? true
    : router.parseUrl(routeWithReturnUrl('/token-entry', state.url));
};

// Token-entry only exists on the legacy backend; the new one has already run SSO.
export const requireLegacyBackend: CanActivateFn = () => {
  if (environment.useLegacyBackend) return true;

  const router = inject(Router);

  return router.parseUrl('/class-picker');
};

/**
 * Skip LstAuthGuard when useLegacyBackend=true - the legacy path enters via
 * token-entry which has no lst-auth dependency.
 */
export const requireAuth: CanActivateFn = (...args) => {
  if (environment.useLegacyBackend) return true;

  const lstAuthGuard = inject(LstAuthGuard);

  return lstAuthGuard.canActivate(...args);
};

/**
 * New flow only: before class-picker/shell, a product-grade must be resolved.
 *
 * Order of resolution:
 *   1. already resolved: proceed.
 *   2. else read `productCode` + `gradeCode` from the URL query, which the Realize
 *      parent passes when it launches this mFE, falling back to the params captured
 *      at startup (the SSO redirect drops them from the URL before this guard runs).
 *   3. no productCode: /program-error.
 *
 * Legacy flow gets its product-grade from the class, so this is a no-op there.
 * Runs before requireLegacyToken so the params aren't lost to the token-entry redirect.
 */
export const requireProgramResolved: CanActivateFn = (route, state) => {
  if (environment.useLegacyBackend) return true;

  const classStore = inject(ClassStore);
  if (classStore.hasResolvedProductGrade()) return true;

  // Guards in a `canActivate` array run concurrently, so this fires alongside
  // LstAuthGuard rather than after it. Pre-login there is no RBS token, so the
  // resolve call would go out as `Bearer null` and fail as a status-0 network
  // error — which flashes the "check your internet connection" alert in the
  // moment before the SSO redirect. Bail out and let LstAuthGuard redirect;
  // this guard re-runs with a token once the auth callback lands.
  const lstUser = inject(LstUserService, { optional: true });
  if (lstUser && !lstUser.getRbsToken()) return true;

  const router = inject(Router);
  const resolver = inject(ProgramResolutionService);

  const programError = (): UrlTree =>
    router.parseUrl(routeWithReturnUrl('/program-error', state.url));
  const launch = launchProductGrade();
  const productCode = route.queryParamMap.get('productCode') ?? launch.productCode;
  const gradeCode = route.queryParamMap.get('gradeCode') ?? launch.gradeCode;
  if (!productCode) return programError();

  return resolver.resolveByProductGrade(productCode, gradeCode).then((resolution) => {
    classStore.setResolvedProgram(resolution);
    return true;
  }, programError);
};

// The shell isn't reachable without a selected class, mirroring legacy's `classID` gate.
export const requireClassSelected: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const classStore = inject(ClassStore);

  if (classStore.selectedClassId() !== null) return true;
  return router.parseUrl(routeWithReturnUrl('/class-picker', state.url));
};

export const requireProjectionClassSelected: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const classStore = inject(ClassStore);
  const callerClassId = route.queryParamMap.get('CallerClassID')?.trim();

  if (classStore.selectedClassId() !== null || callerClassId) return true;
  return router.parseUrl(routeWithReturnUrl('/class-picker', state.url));
};

export const redirectToRoleLanding: RedirectFunction = () => {
  const userStore = inject(UserStore);
  const productVariant = inject(ProductVariantService);
  return roleLandingRoute(userStore.role(), productVariant.isIlit20());
};

// Legacy parity: Groups is hidden from co-teachers (instructor.html:451) and students.
// Every other teacher role, substitutes included, gets in; don't require lead teacher.
export const requireGroupsAccess: CanActivateFn = () => {
  const router = inject(Router);
  const userStore = inject(UserStore);
  const classStore = inject(ClassStore);

  if (userStore.isStudent() || classStore.isStudent()) return router.parseUrl('/library');
  if (classStore.isCoTeacher()) return router.parseUrl('/planner');
  return true;
};

export const requireStudent: CanActivateFn = () => {
  const router = inject(Router);
  const userStore = inject(UserStore);
  const classStore = inject(ClassStore);

  if (userStore.isStudent() || classStore.isStudent()) return true;
  return router.parseUrl('/planner');
};

export const requireTeacher: CanActivateFn = (_route, _state) => {
  const router = inject(Router);
  const userStore = inject(UserStore);
  const classStore = inject(ClassStore);

  if (!userStore.isStudent() && !classStore.isStudent()) return true;
  return router.parseUrl('/library');
};

export const requireProjectionTeacher: CanActivateFn = (route, state) => {
  const projectionApp = inject(ProjectionAppStore);
  const result = requireTeacher(route, state);
  if (result === true) projectionApp.enter();
  else projectionApp.leave();
  return result;
};

// Blocks direct URL access to a tab the nav hides for this class's product and role.
export const requireTabAccess: CanActivateFn = (route) => {
  const tab = route.routeConfig?.path;
  if (!tab || !ALL_TAB_KEYS.includes(tab as TabKey)) return true;

  const router = inject(Router);
  const userStore = inject(UserStore);
  const classStore = inject(ClassStore);
  const productConfig = inject(ProductConfigService);
  const productVariant = inject(ProductVariantService);

  const visible = productConfig.visibleTabs(userStore.role(), {
    isIlit20: productVariant.isIlit20(),
    isCoTeacher: classStore.isCoTeacher(),
  });
  if (visible.includes(tab as TabKey)) return true;
  return router.parseUrl(`/${visible[0]}`);
};
